import type { AnnotationRect, ViewportState } from '/@/types/annotation'

export function canvasToRelative (value: number, displaySize: number): number {
  if (displaySize <= 0) return 0
  return (value / displaySize) * 100
}

export function relativeToCanvas (value: number, displaySize: number): number {
  return (value / 100) * displaySize
}

export function relativeToImage (value: number, naturalSize: number): number {
  return (value / 100) * naturalSize
}

export function imageToRelative (value: number, naturalSize: number): number {
  if (naturalSize <= 0) return 0
  return (value / naturalSize) * 100
}

export function getDisplayScale (viewport: ViewportState): number {
  return viewport.fitScale * viewport.zoom
}

export function getDisplaySize (
  viewport: ViewportState
): { width: number; height: number } {
  const scale = getDisplayScale(viewport)
  return {
    width: viewport.naturalWidth * scale,
    height: viewport.naturalHeight * scale,
  }
}

/** Stage 坐标 → 原图像素坐标 */
export function stageToImage (
  stageX: number,
  stageY: number,
  viewport: ViewportState
): { x: number; y: number } {
  const scale = getDisplayScale(viewport)
  const x = (stageX - viewport.offsetX - viewport.panX) / scale
  const y = (stageY - viewport.offsetY - viewport.panY) / scale
  return { x, y }
}

/** 原图像素 → Stage 坐标 */
export function imageToStage (
  imageX: number,
  imageY: number,
  viewport: ViewportState
): { x: number; y: number } {
  const scale = getDisplayScale(viewport)
  return {
    x: imageX * scale + viewport.offsetX + viewport.panX,
    y: imageY * scale + viewport.offsetY + viewport.panY,
  }
}

export function rectImageToRelative (
  rect: { x: number; y: number; width: number; height: number; rotation?: number },
  naturalWidth: number,
  naturalHeight: number
): AnnotationRect {
  return {
    x: imageToRelative(rect.x, naturalWidth),
    y: imageToRelative(rect.y, naturalHeight),
    width: imageToRelative(rect.width, naturalWidth),
    height: imageToRelative(rect.height, naturalHeight),
    rotation: rect.rotation ?? 0,
  }
}

export function rectRelativeToImage (
  rect: AnnotationRect,
  naturalWidth: number,
  naturalHeight: number
): { x: number; y: number; width: number; height: number; rotation: number } {
  return {
    x: relativeToImage(rect.x, naturalWidth),
    y: relativeToImage(rect.y, naturalHeight),
    width: relativeToImage(rect.width, naturalWidth),
    height: relativeToImage(rect.height, naturalHeight),
    rotation: rect.rotation ?? 0,
  }
}

export function fitToContainer (
  containerW: number,
  containerH: number,
  imageW: number,
  imageH: number
): { scale: number; offsetX: number; offsetY: number } {
  if (imageW <= 0 || imageH <= 0 || containerW <= 0 || containerH <= 0) {
    return { scale: 1, offsetX: 0, offsetY: 0 }
  }
  const scale = Math.min(containerW / imageW, containerH / imageH)
  const displayW = imageW * scale
  const displayH = imageH * scale
  return {
    scale,
    offsetX: (containerW - displayW) / 2,
    offsetY: (containerH - displayH) / 2,
  }
}

export function zoomAtPoint (
  viewport: ViewportState,
  pointerX: number,
  pointerY: number,
  factor: number,
  minZoom = 0.1,
  maxZoom = 10
): Partial<ViewportState> {
  const oldScale = getDisplayScale(viewport)
  const newZoom = Math.min(maxZoom, Math.max(minZoom, viewport.zoom * factor))
  const newScale = viewport.fitScale * newZoom
  if (newScale === oldScale) {
    return { zoom: newZoom }
  }
  const ratio = newScale / oldScale
  const panX = pointerX - (pointerX - viewport.panX - viewport.offsetX) * ratio - viewport.offsetX + viewport.panX
  const panY = pointerY - (pointerY - viewport.panY - viewport.offsetY) * ratio - viewport.offsetY + viewport.panY
  return {
    zoom: newZoom,
    panX: viewport.panX + (pointerX - viewport.offsetX - viewport.panX) * (1 - ratio),
    panY: viewport.panY + (pointerY - viewport.offsetY - viewport.panY) * (1 - ratio),
  }
}
